import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getMe, login as apiLogin, refreshToken as apiRefreshToken } from './api';

const AuthContext = createContext(null);

// Refresh token every 30 minutes (token expires in 24h)
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000;

// Decode JWT to get expiry timestamp (without verification)
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  const refreshToken = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Check if token is still valid (not expired)
      const expiry = getTokenExpiry(token);
      if (expiry && Date.now() > expiry) {
        // Token already expired, can't refresh - logout
        logout();
        return;
      }

      const res = await apiRefreshToken();
      localStorage.setItem('token', res.data.access_token);
    } catch {
      // If refresh fails silently, don't logout - let the 401 interceptor handle it
    }
  }, []);

  const startTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    refreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);
  }, [refreshToken]);

  const stopTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Refresh token when user returns to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('token')) {
        const token = localStorage.getItem('token');
        const expiry = getTokenExpiry(token);
        // If token expires in less than 2 hours, refresh now
        if (expiry && (expiry - Date.now()) < 2 * 60 * 60 * 1000) {
          refreshToken();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshToken]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token is expired before even trying
      const expiry = getTokenExpiry(token);
      if (expiry && Date.now() > expiry) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }

      getMe()
        .then((res) => {
          setUser(res.data);
          startTokenRefresh();
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    return () => stopTokenRefresh();
  }, []);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    localStorage.setItem('token', res.data.access_token);
    const userRes = await getMe();
    setUser(userRes.data);
    localStorage.setItem('user', JSON.stringify(userRes.data));
    startTokenRefresh();
    return userRes.data;
  };

  const logout = () => {
    stopTokenRefresh();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    
    // Support both old single role and new multi-role system
    const userRoles = user.roles || [user.role];
    
    // Supervisor inherits all project_manager permissions (mirrors backend has_role logic)
    const isSupervisor = userRoles.includes('supervisor');
    
    if (typeof roles === 'string') {
      if (roles === 'project_manager' && isSupervisor) return true;
      return userRoles.includes(roles);
    }
    
    // Check if any of the user's roles matches any of the required roles
    if (roles.includes('project_manager') && isSupervisor) return true;
    return roles.some(role => userRoles.includes(role));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
